/**
 * 개발 전용 목 응답 — mockMode 가 켜졌을 때만 동작
 * ──────────────────────────────────────────────────────────────────
 * 백엔드가 죽어 있어 UI 전환 작업이 막히는 것을 피하기 위한 장치입니다.
 *
 * 동작 규칙
 *  · **서버 응답이 오면 절대 개입하지 않습니다.** 요청이 네트워크 단계에서 실패
 *    (연결 거부·타임아웃)했을 때만 픽스처로 대체합니다.
 *  · 픽스처가 없는 경로는 "빈 성공 응답"을 돌려줍니다. 화면이 빈 상태(EmptyState)
 *    로 정상 렌더되는지 확인하기 위함입니다.
 *  · prod 에서는 mockMode 가 항상 false 이므로 등록 자체가 되지 않습니다.
 *
 * 전환이 끝나면 src/dev 폴더째 삭제합니다.
 */

import { instance } from 'common/axios/axios';
import { isMockMode } from './mockMode';

/** 서버 공통 응답 래퍼 (ApiResponse) 모양 */
const ok = (data: unknown) => ({ status: 200, data: { code: 'SUCCESS', message: '', data } });

const nowPlus = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 19);
};

const MEMBERS = [
    { userNo: 1, nickname: '재권', attendanceStatus: 'ATTENDING', role: 'CREATOR', locationSharing: true },
    { userNo: 2, nickname: '민서', attendanceStatus: 'ATTENDING', role: 'MEMBER' },
    { userNo: 3, nickname: '태윤', attendanceStatus: 'ATTENDING', role: 'MEMBER' },
    { userNo: 4, nickname: '지훈', attendanceStatus: 'UNDECIDED', role: 'MEMBER' },
    { userNo: 5, nickname: '하늘', attendanceStatus: 'UNDECIDED', role: 'MEMBER' },
    { userNo: 6, nickname: '수아', attendanceStatus: 'NOT_ATTENDING', role: 'MEMBER' },
];

const MEETING = {
    meetingId: 'MTG_MOCK_0001',
    meetingName: '금요일 저녁 번개',
    description: '오랜만에 다 같이 한잔',
    meetingType: 'DAY',
    category: 'FOOD',
    creatorNo: 1,
    creatorNickname: '재권',
    inviteCode: 'CREW-4K7QX2',
    memberCount: 6,
    attendingCount: 3,
    undecidedCount: 2,
    isRegularMeeting: false,
    meetingTime: '19:00',
    place: {
        placeId: 'PLC_MOCK_1',
        placeName: '소금집 델리',
        placeAddress: '서울 마포구 월드컵로 12',
        latitude: '37.5563',
        longitude: '126.9139',
    },
    schedule: { scheduleId: 1, startDatetime: nowPlus(0), endDatetime: nowPlus(0) },
};

/** 경로 → 픽스처. 앞에서부터 첫 일치를 씁니다. */
const FIXTURES: { match: RegExp; body: () => unknown }[] = [
    { match: /\/meetings\/list$/, body: () => [MEETING] },
    { match: /\/meetings\/MTG_[^/]+\/members/, body: () => MEMBERS },
    { match: /\/meetings\/MTG_[^/]+$/, body: () => MEETING },
    { match: /\/meetings\/my-pending-invites/, body: () => [] },
    { match: /\/payments/, body: () => [] },
    { match: /\/schedules/, body: () => [] },
    { match: /\/notification-settings/, body: () => ({ pushEnabled: true }) },
];

let installed = false;

export const setupMockApi = (): void => {
    if (installed || !isMockMode()) return;
    installed = true;

    instance.interceptors.response.use(
        (res) => res,
        (error) => {
            // 서버가 응답을 준 경우(4xx/5xx 포함) 는 그대로 흘려보냅니다 — 개입 금지
            if (error?.response) return Promise.reject(error);

            const url: string = error?.config?.url || '';
            const hit = FIXTURES.find((f) => f.match.test(url));
            const body = hit ? hit.body() : null;
            // eslint-disable-next-line no-console
            console.warn('[mock]', url, hit ? '픽스처 대체' : '빈 응답 대체');
            return Promise.resolve({
                ...ok(body),
                statusText: 'OK',
                headers: {},
                config: error?.config,
            });
        },
    );
};
